import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { InspectionReportStatusBadge } from '@/components/shared/InspectionReportStatusBadge';
import {
  useJobInspectionReports,
  useCreateInspectionReport,
  useDeleteInspectionReport,
  useSetInspectionReportSent,
  type InspectionReport,
} from '@/hooks/useInspectionReports';

// "Inspections" tab content on the Job detail page. Lists the job's inspection
// reports and opens the editor; mirrors how breakdown/invoices hang off a job.
export function InspectionReportsSection({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const { data: reports, isLoading } = useJobInspectionReports(jobId);
  const createReport = useCreateInspectionReport(jobId);
  const deleteReport = useDeleteInspectionReport(jobId);
  const setSent = useSetInspectionReportSent(jobId);

  const openReport = (reportId: string) => navigate(`/jobs/${jobId}/inspection-reports/${reportId}`);

  const handleNew = async () => {
    const report = await createReport.mutateAsync({});
    openReport(report.id);
  };

  const toggleSent = (report: InspectionReport) =>
    setSent.mutate({ id: report.id, sent: report.status !== 'sent' });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Inspection Reports</h2>
        <Button onClick={handleNew} disabled={createReport.isPending}>
          {createReport.isPending ? 'Creating…' : 'New report'}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !reports || reports.length === 0 ? (
        <div className="rounded border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          No inspection reports yet. Click <span className="font-medium">New report</span> to start one.
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 rounded border border-gray-200">
          {reports.map((report) => {
            const isSent = report.status === 'sent';
            // Only the row being changed shows a pending label — the mutation is
            // shared across every row, so isPending alone would grey out all of
            // them on any click.
            const isUpdating = setSent.isPending && setSent.variables?.id === report.id;

            return (
              <li key={report.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <button className="min-w-0 flex-1 text-left" onClick={() => openReport(report.id)}>
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-gray-900">
                      {report.title || 'Untitled report'}
                    </span>
                    <InspectionReportStatusBadge status={report.status} sentAt={report.sentAt} />
                  </div>
                  <div className="text-xs text-gray-500">
                    updated {new Date(report.updatedAt).toLocaleDateString()}
                  </div>
                </button>
                <div className="flex shrink-0 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isUpdating}
                    onClick={() => toggleSent(report)}
                  >
                    {isUpdating ? 'Saving…' : isSent ? 'Mark as draft' : 'Mark as sent'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openReport(report.id)}>
                    Open
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (confirm('Delete this inspection report?')) deleteReport.mutate(report.id);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
